import React, { useContext } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ExpeditionDetailV4OutDTO, ExpeditionStatusEnum, FeaturesEnum, PermissionTypesEnum } from '@api/ringil-api.v2';
import { Panel, Paragraph } from '@components/ringil3/styles';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgLkw from '@components/ringil3/icons/heading/Lkw';
import {
  useCreateAssignCarrierSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/AssignCarrierSidebar.tsx';
import { useRequestCarrierSidebar } from '@components/ringil3/Features/Expedition/Sidebars/RequestCarrierSidebar.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { getAiCarrierAutoSelectionDto, isLadenFactorEligible } from '@utils/expeditionUtils.ts';
import { RowForTagsAndBadges } from '@components/styles.tsx';
import LadenFactorTag from '@components/framework/tags/LadenFactorTag.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  expedition: ExpeditionDetailV4OutDTO;
  onReload: () => void;
  secured?: SecuredConfig;
}

const TabExpeditionStateNew: React.FC<ComponentProps> = ({
                                                           expedition, onReload = () => {
  }, secured,
                                                         }) => {
  const { tr } = useTranslationLgs();

  const { hasFeature } = useContext(AuthenticatedUserContext);

  const { AssignCarrierSidebar, setVisible: setVisibleAssignCarrierSidebar } = useCreateAssignCarrierSidebar();
  const { RequestCarrierSidebar, setVisible: setVisibleRequestCarrierSidebar } = useRequestCarrierSidebar();
  const { decorate } = useSecuredDecoration(secured);

  const ladenFactorFlag = useSecuredFlag('state-new-laden-factor', {
    kind: 'visibility',
    description: 'Default laden factor tag',
    features: [FeaturesEnum.LADEN_FACTOR],
  });
  const carrierSectionFlag = useSecuredFlag('state-new-carrier', {
    kind: 'visibility',
    description: 'Carrier assignment section (NEW expeditions)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
    state: [
      {
        label: 'expeditionStatus === NEW',
        satisfied: expedition.expeditionStatus === ExpeditionStatusEnum.NEW,
        description: 'Shown only while the shipment is New, before any carrier is asked or assigned',
        byDesign: true,
      },
    ],
  });
  const carrierButtonsFlag = useSecuredFlag('state-new-carrier-buttons', {
    kind: 'visibility',
    description: 'Request / Assign carrier buttons',
    permissions: [PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW, PermissionTypesEnum.PERMISSION_CARRIER_GROUP_READ],
  });

  return (
    <>{decorate(<>
      {/* assign carrier*/}
      <SecuredArea id="state-new-carrier" label="Carrier assignment (NEW)" description="Carrier section of a new shipment: the default laden factor and the request or assign carrier buttons" visible={carrierSectionFlag}>
        <COHeadline variant={'h2'} title={tr(`ExpeditionDetail.carrier`, `Carrier`)} icon={<SvgLkw />}></COHeadline>
        <RowForTagsAndBadges>
          <SecuredArea
            id="state-new-laden-factor"
            label="LadenFactorTag"
            description="The shipment's default laden factor"
            visible={[
              ladenFactorFlag,
              { type: 'data', label: 'defaultLadenFactor > 0', satisfied: !isNullOrUndefined(expedition.defaultLadenFactor) && expedition.defaultLadenFactor > 0 },
            ]}
          >
            <LadenFactorTag ladenFactor={expedition.defaultLadenFactor} />
          </SecuredArea>
        </RowForTagsAndBadges>
        <SecuredArea id="state-new-carrier-buttons" label="Request / Assign carrier buttons" description="Buttons to request quotes from carriers or assign a carrier directly" visible={carrierButtonsFlag}>
          <Panel>
            <div className={'flex flex-row gap-4 items-center'}>
              <Paragraph>{tr(`ExpeditionDetail.doYouHaveNotAssignedCarrier`, `Do you have not assigned carrier`)}</Paragraph>
              <RingilButton
                mode={'filled-rectangle'}
                size={'big'}
                label={tr(`TabExpeditionStateNew.requestCarrier`, `Request carrier`)}
                onClick={() => setVisibleRequestCarrierSidebar(true)}
                dataTestId='ringilTab-expedition-requestCarrier'
              />
              {/* RD-2978: a shipment from a Linka goes only to its contracted carriers, never assigned by hand */}
              {!expedition.tmplV2Id && (
                <RingilButton
                  mode={'filled-rectangle'}
                  size={'big'}
                  label={tr(`TabExpeditionStateNew.assignCarrier`, `Assign carrier`)}
                  onClick={() => setVisibleAssignCarrierSidebar(true)}
                  dataTestId='ringilTab-expedition-assignCarrier'
                />
              )}
            </div>
          </Panel>
        </SecuredArea>
      </SecuredArea>
      <RequestCarrierSidebar
        onContinue={() => {
          onReload();
        }}
        handlingUnitCount={expedition.handlingUnitCount}
        ladenFactorEligible={isLadenFactorEligible(expedition, hasFeature([FeaturesEnum.LADEN_FACTOR]))}
        aiCarrierAutoSelectionDto={getAiCarrierAutoSelectionDto(expedition)}
        expedition={expedition}
      />
      <AssignCarrierSidebar
        applicationId={expedition.applicationId}
        customerId={expedition.customerId}
        expeditionId={expedition.id}
        onContinue={() => {
          onReload();
        }}
        aiCarrierAutoSelectionDto={getAiCarrierAutoSelectionDto(expedition)}
      />
    </>)}</>
  );
};

export default TabExpeditionStateNew;
